import { AxiosAdapter, InternalAxiosRequestConfig } from 'axios';
import {
  getMockBoletos,
  getMockFaturasVencidas,
  getMockBoletosAvulsos,
  getMockDownloads,
  getMockParcelas,
  getMockUsers,
  mockAcompanhamento,
  mockContratos,
  mockDadosVenda,
  mockEmpreendimentosAssistencia,
  mockFunctionalities,
  mockIndicesIgpm,
  mockIndicesIncc,
  mockToken,
  mockUnits,
  mockUserDataByCfo,
} from './fixtures';

type MockResponse = {
  status: number;
  data: unknown;
};

const buildUrl = (config: InternalAxiosRequestConfig) => {
  const rawUrl = config.url ?? '/';
  const base = config.baseURL ?? 'https://api.soter.com.br/';
  return new URL(rawUrl, base);
};

const parseQuery = (url: URL, key: string) => url.searchParams.get(key) ?? '';

const getPath = (url: URL) => url.pathname.replace(/^\/+/, '');

const parseJsonBody = (body: unknown) => {
  if (typeof body === 'string') {
    try {
      return JSON.parse(body) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  if (body && typeof body === 'object') {
    return body as Record<string, unknown>;
  }

  return {};
};

const parsePeriodoDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getUserByParams = (url: URL) => {
  const codcfo =
    parseQuery(url, '_codcfo') ||
    parseQuery(url, '_codcfo2') ||
    parseQuery(url, '_codcfo3');
  const users = getMockUsers();
  return users.find((user) => user.codcfo === codcfo) ?? users[0];
};

const handleAuthEndpoints = (
  path: string,
  method: string,
  url: URL,
): MockResponse | null => {
  if (path === 'token' && method === 'post') {
    return {
      status: 200,
      data: {
        access_token: mockToken,
        token_type: 'bearer',
        expires_in: 86400,
      },
    };
  }

  if (path === 'Cliente/Login' && method === 'get') {
    return { status: 200, data: getMockUsers() };
  }

  if (path === 'Cliente/BuscaFuncionalidadesAcesso' && method === 'get') {
    return { status: 200, data: mockFunctionalities };
  }

  if (path === 'Cliente/DadosCadastrais' && method === 'get') {
    const user = getUserByParams(url);
    return {
      status: 200,
      data: mockUserDataByCfo[user.codcfo] ?? mockUserDataByCfo['1001'],
    };
  }

  if (path === 'Cliente/ResetarSenha' && method === 'get') {
    return { status: 200, data: 'senha-temp-123' };
  }

  if (path === 'Cliente/AlterarSenha' && method === 'get') {
    return { status: 200, data: true };
  }

  return null;
};

const handleFinanceEndpoints = (
  path: string,
  method: string,
  url: URL,
): MockResponse | null => {
  if (path === 'ClienteVenda/BuscaClienteSubunidade' && method === 'get') {
    return { status: 200, data: mockUnits };
  }

  if (path === 'ClienteVenda/BuscaBoletos' && method === 'get') {
    return { status: 200, data: getMockBoletos() };
  }

  if (path === 'ClienteVenda/BuscaBoletosVencidos' && method === 'get') {
    return { status: 200, data: getMockFaturasVencidas() };
  }

  if (path === 'ClienteVenda/BuscaBoletoAvulso' && method === 'get') {
    return { status: 200, data: getMockBoletosAvulsos() };
  }

  if (path === 'ClienteVenda/BuscaBoletoPdf' && method === 'get') {
    return { status: 200, data: 'JVBERi0xLjQKJcTl8uXr' };
  }

  if (path === 'ClienteVenda/RegistraLog' && method === 'get') {
    return { status: 200, data: true };
  }

  if (path === 'ClienteVenda/BuscaParcelas' && method === 'get') {
    const numVenda = Number(parseQuery(url, '_numVenda2') || '0');
    return {
      status: 200,
      data: getMockParcelas().filter((item) => item.numVenda === numVenda),
    };
  }

  if (path === 'ClienteVenda/BuscaParcelasPeriodo' && method === 'get') {
    const numVenda = Number(parseQuery(url, '_numVenda3') || '0');
    const dataInicio = parsePeriodoDate(parseQuery(url, '_dataInicio'));
    const dataFim = parsePeriodoDate(parseQuery(url, '_dataFim'));

    const filtered = getMockParcelas()
      .filter((item) => item.numVenda === numVenda && item.dataPagamento)
      .filter((item) => {
        const dataPagamento = parsePeriodoDate(item.dataPagamento ?? '');
        if (!dataPagamento) {
          return false;
        }
        if (!dataInicio || !dataFim) {
          return true;
        }
        return dataPagamento >= dataInicio && dataPagamento <= dataFim;
      });

    return { status: 200, data: filtered };
  }

  if (path === 'ClienteVenda/DadosClienteVenda' && method === 'get') {
    return { status: 200, data: mockDadosVenda };
  }

  if (
    path === 'ClienteVenda/BuscaBloqueioFichaFinanceira' &&
    method === 'get'
  ) {
    return {
      status: 200,
      data: {
        bloqueado: false,
        dtFinal: '',
      },
    };
  }

  if (path === 'ClienteVenda/BuscaAcompanhamentoObra' && method === 'get') {
    return { status: 200, data: mockAcompanhamento };
  }

  if (path === 'ClienteVenda/BuscaIndice' && method === 'get') {
    const indice = parseQuery(url, '_Indice').toUpperCase();
    if (indice === 'IGPM') {
      return { status: 200, data: mockIndicesIgpm };
    }

    return { status: 200, data: mockIndicesIncc };
  }

  return null;
};

const handleDocumentsAssistenceEndpoints = (
  path: string,
  method: string,
): MockResponse | null => {
  if (path === 'Cliente/buscaContrato' && method === 'get') {
    return { status: 200, data: mockContratos };
  }

  if (
    path === 'ClienteVenda/BuscarDownloadsEmpreendimento' &&
    method === 'get'
  ) {
    return { status: 200, data: getMockDownloads() };
  }

  if (path === 'Empreendimento/DadosUnidades' && method === 'get') {
    return { status: 200, data: mockEmpreendimentosAssistencia };
  }

  if (path === 'Empreendimento/BuscaAssistencia' && method === 'get') {
    return {
      status: 200,
      data: ['https://assistencia.mock.soter.com.br/chamado/1'],
    };
  }

  return null;
};

const handleInfrastructureEndpoints = (
  url: URL,
  method: string,
  config: InternalAxiosRequestConfig,
): MockResponse | null => {
  if (url.hostname === 'api.ipify.org') {
    return { status: 200, data: '127.0.0.1' };
  }

  if (url.hostname === 'soter-six.vercel.app' && method === 'post') {
    return { status: 200, data: { ok: true } };
  }

  if (method === 'post') {
    const body = parseJsonBody(config.data);
    if (body.body) {
      return { status: 200, data: { ok: true } };
    }
  }

  return null;
};

const handleMockRequest = (
  config: InternalAxiosRequestConfig,
): MockResponse => {
  const url = buildUrl(config);
  const method = (config.method ?? 'get').toLowerCase();
  const path = getPath(url);

  const infraResponse = handleInfrastructureEndpoints(url, method, config);
  if (infraResponse) {
    return infraResponse;
  }

  const authResponse = handleAuthEndpoints(path, method, url);
  if (authResponse) {
    return authResponse;
  }

  const financeResponse = handleFinanceEndpoints(path, method, url);
  if (financeResponse) {
    return financeResponse;
  }

  const docsResponse = handleDocumentsAssistenceEndpoints(path, method);
  if (docsResponse) {
    return docsResponse;
  }

  throw new Error(
    `[mock-api] endpoint nao mapeado: ${method.toUpperCase()} ${url.toString()}`,
  );
};

export const createMockAdapter = (): AxiosAdapter => {
  return (config) => {
    const response = handleMockRequest(config);

    return Promise.resolve({
      data: response.data,
      status: response.status,
      statusText:
        response.status >= 200 && response.status < 300 ? 'OK' : 'Error',
      headers: {},
      config,
    });
  };
};
